import {CourseStep, CourseStepAttachment} from "../../../types";
import React, {FC} from "react";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {Box, Button, IconButton} from "@mui/material";
import {Delete} from "@mui/icons-material";
import FormInputText from "../../../admin/components/Forms/FormInputText";
import Editor from "../../../components/Forms/Editor";
import {useUploadFileMutation} from "../../../api/admin/api";
import TextInput from "../../../components/Forms/TextInput";


export interface EditStepFormData {
  name: string
  order: number,
  content: string,
  youtubeLink?: string,
  uploads?: FileList,
  attachments?: {
    url: string,
    name: string,
    main: boolean
  }[]
}

interface Props {
  data: CourseStep
  onSubmit: (data: EditStepFormData) => Promise<void>
  onDeleteAttachment: (attachment: CourseStepAttachment) => Promise<void>
}

const EditStepForm: FC<Props> = ({data, onSubmit, onDeleteAttachment}) => {
  const {t} = useTranslation();
  const {
    handleSubmit,
    control,
    formState: {errors},
    register
  } = useForm<EditStepFormData>({reValidateMode: 'onChange'});
  const [uploadFile] = useUploadFileMutation();

  const existingVideo = data?.attachments?.find((a) => a.main);
  const existingFiles = data?.attachments?.filter((a) => !a.main) ?? [];

  const submitForm = handleSubmit(async (formData) => {
    const newAttachments: NonNullable<EditStepFormData['attachments']> = [];

    if (formData.uploads) {
      for (let file of Array.from(formData.uploads)) {
        const resp = await uploadFile(file).unwrap()
        newAttachments.push({url: resp.link, main: false, name: "upload"})
      }
    }

    if (formData.youtubeLink) {
      newAttachments.push({url: formData.youtubeLink, main: true, name: "video"})
    }

    await onSubmit({
      name: formData.name,
      order: formData.order,
      content: formData.content,
      attachments: newAttachments,
    })
  })
  return (
    <form onSubmit={submitForm}>
      <Box className="pb-4">
        <FormInputText
          value={data?.name || ''}
          label={t('fields.Name')}
          name="name"
          control={control}
          required={true}
          minLength={3}
          maxLength={255}/>
      </Box>
      <Box className="pb-4">
        <FormInputText
          type="number"
          value={data?.order || ''}
          label={t('fields.Order')}
          name="order"
          control={control}
          required={true}
        />
      </Box>
      <Box className="pb-4">
        <FormInputText
          value={''}
          label={t('fields.YoutubeLink')}
          name="youtubeLink"
          control={control}
          required={false}
          minLength={3}
          maxLength={255}/>
      </Box>
      <Editor control={control} name="content" label={t('fields.Content')} required={true} value={data?.content || ''}/>

      {(existingVideo || existingFiles.length > 0) && (
        <Box className="pt-4 pb-2">
          <label className="text-gray-600 font-medium block pb-2">{t('fields.Attachments')}</label>
          <ul className="list-none pl-0">
            {existingVideo && (
              <li key={`video-${existingVideo.id}`} className="flex items-center justify-between py-1 border-b border-gray-100">
                <a href={existingVideo.url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline break-all">
                  {existingVideo.url}
                </a>
                <IconButton color="error" size="small" onClick={() => onDeleteAttachment(existingVideo)} aria-label="delete">
                  <Delete />
                </IconButton>
              </li>
            )}
            {existingFiles.map((attachment) => (
              <li key={`file-${attachment.id}`} className="flex items-center justify-between py-1 border-b border-gray-100">
                <a href={attachment.url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline break-all">
                  {attachment.url}
                </a>
                <IconButton color="error" size="small" onClick={() => onDeleteAttachment(attachment)} aria-label="delete">
                  <Delete />
                </IconButton>
              </li>
            ))}
          </ul>
        </Box>
      )}

      <TextInput
        label={t('fields.Logo')}
        name="uploads"
        type="file"
        multiple
        register={register}
        error={errors.uploads}
        options={{required: false}}
      />
      <Box className="pt-4 text-right">
        <Button type="submit" size="medium" variant="contained">
          {t('forms.actions.Save')}
        </Button>
      </Box>
    </form>
  )
}

export default EditStepForm
